import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Users, MailOpen, Mail, Percent } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import StatTileCard from '../../../components/ui/StatTileCard';
import Button from '../../../components/ui/Button';
import ReportExportButtons from '../../../components/ui/ReportExportButtons';
import { parseTableParams } from '../../../utils/Utils';
import {
  getAnnouncementReadSummary,
  useAnnouncementReadersApi,
  readsExportUrl,
  buildReadsExportLabels,
  buildReadsExportParams,
  buildReadersColumns,
  formatOpenRate,
} from '../config';

// Tek kaydırma: dış kap sabit yükseklikte ve taşmaz; kaydırma yalnız DataTable satırlarında (gövde yüksekliği kabın
// ölçülen yüksekliğinden hesaplanır — PerformanceTrendsPage deseni).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Top = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Info = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => `${theme.colors.primary.main}14`};
`;

const ErrorRow = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  background: ${({ theme }) => theme.colors.auxiliar.dangerSoft};
`;

const TableArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ViewToggle = styled.span`
  display: inline-flex;
  gap: 8px;
  margin-right: 8px;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $opened, theme }) => ($opened ? theme.colors.secondary.dark : theme.colors.text.muted)};
  background: ${({ $opened, theme }) => `${$opened ? theme.colors.secondary.main : theme.colors.text.muted}1F`};
`;

const VIEW_PEOPLE = 'people';
const VIEW_DEPARTMENTS = 'departments';
// Araç çubuğu + başlık satırı + sayfalama için ayrılan pay (px).
const TABLE_CHROME = 150;

// Açılma takibinden ÖNCE yayımlanmış duyuru (inceleme I2): sayılar yerine bilgi satırı; Okunma uçları hiç çağrılmaz.
export default function AnnouncementReadsTab({ announcementId, trackingEnabled = true }) {
  const { t } = useTranslation();
  if (!trackingEnabled) {
    return (
      <Container data-testid="announcement-reads-tab">
        <Info data-state="not-tracked">{t('announcement.reads.notTrackedInfo')}</Info>
      </Container>
    );
  }
  return <ReadsTabBody announcementId={announcementId} />;
}

function ReadsTabBody({ announcementId }) {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [view, setView] = useState(VIEW_PEOPLE);
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [filterList, setFilterList] = useState([]);
  const [height, setHeight] = useState(0);
  const tableRef = useRef(null);

  const { fetching, data, pagination } = useAnnouncementReadersApi(announcementId, queryParams);

  useEffect(() => {
    let alive = true;
    setSummaryFailed(false);
    getAnnouncementReadSummary(announcementId).then((res) => {
      if (!alive) return;
      if (res?.data?.status === false || !res?.data?.data) {
        setSummary(null);
        setSummaryFailed(true);
        return;
      }
      setSummary(res.data.data);
    });
    return () => { alive = false; };
  }, [announcementId]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const departments = useMemo(() => summary?.departments || [], [summary]);

  const renderStatus = useCallback(
    (opened, text) => <StatusBadge $opened={opened}>{text}</StatusBadge>,
    [],
  );

  const peopleColumns = useMemo(
    () => buildReadersColumns({ t, departments, rows: data, locale: i18n.language, renderStatus }),
    [t, departments, data, i18n.language, renderStatus],
  );

  const departmentColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'departmentName', label: t('announcement.reads.department'), options: { filter: false, sort: true } },
    { name: 'audienceCount', label: t('announcement.reads.audience'), options: { filter: false, sort: true } },
    { name: 'openedCount', label: t('announcement.reads.openedCount'), options: { filter: false, sort: true } },
    { name: 'notOpenedCount', label: t('announcement.reads.notOpenedCount'), options: { filter: false, sort: true } },
    {
      name: 'openRate', label: t('announcement.reads.openRate'),
      options: { filter: false, sort: true, customBodyRender: (v) => formatOpenRate(v, i18n.language) },
    },
  ], [t, i18n.language]);

  const departmentRows = useMemo(() => departments.map((d) => ({
    id: d.departmentId || 'none',
    departmentName: d.departmentName || t('announcement.reads.noDepartment'),
    audienceCount: d.audienceCount,
    openedCount: d.openedCount,
    notOpenedCount: d.notOpenedCount,
    openRate: d.openRate,
  })), [departments, t]);

  const handleTableChanged = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, peopleColumns));
  }, [peopleColumns]);

  const getLabels = useCallback(() => buildReadsExportLabels(t), [t]);

  // Dosya her zaman iki bölümlüdür; süzgeç/sıralama kişi görünümünün son uygulanan hâli (departman görünümünde de).
  const toolbarActions = useMemo(() => (
    <>
      <ViewToggle>
        <Button
          type="button"
          variant={view === VIEW_PEOPLE ? 'primary' : 'outline'}
          data-view={VIEW_PEOPLE}
          onClick={() => setView(VIEW_PEOPLE)}
        >
          {t('announcement.reads.viewPeople')}
        </Button>
        <Button
          type="button"
          variant={view === VIEW_DEPARTMENTS ? 'primary' : 'outline'}
          data-view={VIEW_DEPARTMENTS}
          onClick={() => setView(VIEW_DEPARTMENTS)}
        >
          {t('announcement.reads.viewDepartments')}
        </Button>
      </ViewToggle>
      <ReportExportButtons
        url={readsExportUrl(announcementId)}
        params={buildReadsExportParams(queryParams)}
        getLabels={getLabels}
        formats={['xlsx', 'pdf']}
        formatLabels={{ xlsx: t('announcement.reads.exportExcel'), pdf: t('announcement.reads.exportPdf') }}
        keepSort
      />
    </>
  ), [view, t, announcementId, queryParams, getLabels]);

  const bodyMaxHeight = height ? `${Math.max(200, height - TABLE_CHROME)}px` : undefined;

  const s = summary;
  return (
    <Container data-testid="announcement-reads-tab">
      <Top>
        {summaryFailed ? (
          <ErrorRow data-state="summary-failed">{t('common.error')}</ErrorRow>
        ) : s ? (
          <Tiles data-testid="read-tiles">
            <StatTileCard icon={Users} value={s.audienceCount} label={t('announcement.reads.audience')} />
            <StatTileCard
              icon={MailOpen}
              value={s.openedCount}
              label={t('announcement.reads.openedCount')}
              sublabel={`${t('announcement.reads.web')}: ${s.openedOnWebCount} · ${t('announcement.reads.mobile')}: ${s.openedOnMobileCount}`}
            />
            <StatTileCard icon={Mail} value={s.notOpenedCount} label={t('announcement.reads.notOpenedCount')} />
            <StatTileCard icon={Percent} value={formatOpenRate(s.openRate, i18n.language)} label={t('announcement.reads.openRate')} />
          </Tiles>
        ) : null}
        <Hint>{t('announcement.reads.liveAudienceHint')}</Hint>
      </Top>

      <TableArea ref={tableRef}>
        {/* İki görünüm de bağlı kalır (kişi tablosunun süzgeç/sıralama durumu görünüm değişince kaybolmasın); görünen tek tablo. */}
        <div style={{ display: view === VIEW_PEOPLE ? 'block' : 'none' }}>
          <DataTable
            title={t('announcement.reads.viewPeople')}
            fetching={fetching}
            columns={peopleColumns}
            data={data}
            customIdSelector="employeeId"
            totalRowCount={pagination.totalRowCount}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            filterList={filterList}
            onFilterConfirm={setFilterList}
            onTableChanged={handleTableChanged}
            toolbarActions={view === VIEW_PEOPLE ? toolbarActions : null}
            tableBodyMaxHeight={bodyMaxHeight}
            download={false}
          />
        </div>
        <div style={{ display: view === VIEW_DEPARTMENTS ? 'block' : 'none' }}>
          <DataTable
            title={t('announcement.reads.viewDepartments')}
            fetching={!s && !summaryFailed}
            columns={departmentColumns}
            data={departmentRows}
            totalRowCount={departmentRows.length}
            serverSide={false}
            disableFilters
            toolbarActions={view === VIEW_DEPARTMENTS ? toolbarActions : null}
            tableBodyMaxHeight={bodyMaxHeight}
            download={false}
          />
        </div>
      </TableArea>
    </Container>
  );
}
